/**
 * Offline demo backend.
 *
 * Answers the same endpoints the real API does, from `demoData.json` plus an
 * in-memory store, so the app is fully navigable with no server running. This
 * is what the shareable Android demo build uses.
 *
 * It is deliberately a *backend*, not a set of canned screens: a donation
 * created in the demo gets a quote, a reference and a payment, then appears in
 * My Donations, moves through the tracking timeline and counts toward the
 * impact totals. Someone exploring the build sees the product behave, not a
 * slideshow.
 *
 * Business rules are imported from `@mfa/domain` — the same state machine and
 * money arithmetic the server uses — so the demo cannot drift into showing
 * something the real product would not do.
 */

import {
  TRACKING_STEPS,
  canShowProofPhotos,
  formatClock,
  matchesFilter,
  orderStatusMeta,
  serviceSlotLabel,
  deliveryDates,
  tiffinEndDate,
  tiffinStatusLabel,
  type AppConfig,
  type CreateDonationRequest,
  type CreateTiffinOrderRequest,
  type DeliveryAddress,
  type DistributionProof,
  type Donation,
  type DonationFilter,
  type DonationSort,
  type DonationSummary,
  type DonationTracking,
  type DonorImpact,
  type DonorProfile,
  type FulfilmentType,
  type HowItWorksStep,
  type ImpactStats,
  type Meal,
  type Money,
  type NotificationItem,
  type OrderStatus,
  type Paginated,
  type PaymentIntent,
  type PaymentMethodOption,
  type Quote,
  type ServiceSchedule,
  type ServiceSlot,
  type ServiceWindow,
  type SlotAvailability,
  type TiffinAvailability,
  type TiffinConfig,
  type TiffinCustomer,
  type TiffinLine,
  type TiffinMenu,
  type TiffinOrder,
  type TiffinOrderSummary,
  type TiffinQuote,
  type TiffinQuoteRequest,
} from '@mfa/domain';

import raw from './demoData.json';

/** Throw the same error shape the HTTP client builds from a real response. */
function fail(status: number, code: string, message: string): never {
  const error = new Error(message) as Error & { status: number; code: string };
  error.status = status;
  error.code = code;
  throw error;
}

// ------------------------------------------------------------------- helpers

const DAY = 86_400_000;
const START = Date.now();

const iso = (offsetMs: number): string => new Date(START + offsetMs).toISOString();
const isoDate = (days: number): string => new Date(START + days * DAY).toISOString().slice(0, 10);

const money = (amountMinor: number): Money => ({ amountMinor, currency: 'PKR' });

/** Reference in the same opaque shape the server issues. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function newReference(): string {
  let body = '';
  for (let i = 0; i < 8; i += 1) {
    body += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return `CH-${body}`;
}

const uid = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

// ---------------------------------------------------------------- the store

interface DemoDonation {
  id: string;
  publicReference: string;
  mealId: string;
  peopleCount: number;
  purpose: Donation['purpose'];
  purposeDetails: string | null;
  donationDate: string;
  distributionDate: string;
  orderStatus: OrderStatus;
  anonymous: boolean;
  shareDistributionPhotos: boolean;
  paymentMethodCode: string;
  transactionReference: string | null;
  /** Null when the donor gave no name, which is now a supported case. */
  donorName: string | null;
  deliveryFeeMinor?: number;
  fulfilmentType?: FulfilmentType;
  recipientName?: string | null;
  deliveryInstruction?: string | null;
  requiresDistributionEvidence?: boolean;
  /** Times each reached step happened, for the tracking timeline. */
  history: Partial<Record<OrderStatus, string>>;
  distribution?: {
    location: string | null;
    partnerOrganization: string | null;
    note: string | null;
    peopleServed: number;
    photos: string[];
  };
}

interface DemoNotification extends Omit<NotificationItem, 'readAt'> {
  readAt: string | null;
}

const data = raw as unknown as {
  stats: Omit<ImpactStats, 'generatedAt'>;
  config: AppConfig;
  howItWorks: HowItWorksStep[];
  meals: Meal[];
  paymentMethods: PaymentMethodOption[];
  profile: DonorProfile;
  donations: {
    id: string;
    publicReference: string;
    mealId: string;
    peopleCount: number;
    purpose: Donation['purpose'];
    purposeDetails: string | null;
    donationDayOffset: number;
    distributionDayOffset: number;
    orderStatus: OrderStatus;
    anonymous: boolean;
    shareDistributionPhotos: boolean;
    paymentMethodCode: string;
    transactionReference: string | null;
    distribution?: {
      location?: string;
      partnerOrganization?: string;
      note?: string;
      peopleServed?: number;
      photos?: string[];
    };
  }[];
  notifications: {
    id: string;
    type: NotificationItem['type'];
    title: string;
    body: string;
    hoursAgo: number;
    read: boolean;
    donationReference: string | null;
  }[];
  tiffin: {
    settings: {
      enabled: boolean;
      maxDaysAhead: number;
      maxPersons: number;
      minDays: number;
      schedules: ServiceSchedule[];
      slots: ServiceWindow[];
      pickup: { enabled: boolean; address: string; note: string | null };
      delivery: { enabled: boolean; feeMinor: number; cities: string[] };
    };
    sections: {
      id: string;
      name: string;
      nameUr: string | null;
      description: string | null;
      descriptionUr: string | null;
      sortOrder: number;
      items: {
        id: string;
        name: string;
        nameUr: string | null;
        description: string;
        descriptionUr: string | null;
        priceMinor: number;
        serviceSlots: ServiceSlot[];
        imageUrl: string | null;
        maxQuantity: number;
      }[];
    }[];
    orders: {
      id: string;
      publicReference: string;
      placedDayOffset: number;
      serviceDayOffset: number;
      serviceSlot: ServiceSlot;
      persons: number;
      days: number;
      schedule: ServiceSchedule;
      items: { menuItemId: string; quantity: number }[];
      fulfilmentType: FulfilmentType;
      orderStatus: OrderStatus;
      address: DeliveryAddress | null;
      note: string | null;
      paymentMethodCode: string;
      transactionReference: string | null;
    }[];
  };
};

/**
 * Build a plausible history for a donation at a given status.
 *
 * Timestamps run from the donation date up to now, so the tracking timeline
 * reads like something that actually happened over time.
 */
function buildHistory(status: OrderStatus, donationDate: string): DemoDonation['history'] {
  const reachedIndex = TRACKING_STEPS.indexOf(status);
  const steps = reachedIndex >= 0 ? TRACKING_STEPS.slice(0, reachedIndex + 1) : ['NEW' as const];
  const start = new Date(donationDate).getTime();
  const span = Math.max(DAY, (START - start) * 0.8);

  const history: DemoDonation['history'] = {};
  steps.forEach((step, index) => {
    history[step] = new Date(start + (span / Math.max(1, steps.length - 1)) * index).toISOString();
  });
  return history;
}

const profile: DonorProfile = JSON.parse(JSON.stringify(data.profile));

const donations: DemoDonation[] = data.donations.map((d) => {
  const donationDate = iso(d.donationDayOffset * DAY);
  return {
    id: d.id,
    publicReference: d.publicReference,
    mealId: d.mealId,
    peopleCount: d.peopleCount,
    purpose: d.purpose,
    purposeDetails: d.purposeDetails,
    donationDate,
    distributionDate: isoDate(d.distributionDayOffset),
    orderStatus: d.orderStatus,
    anonymous: d.anonymous,
    shareDistributionPhotos: d.shareDistributionPhotos,
    paymentMethodCode: d.paymentMethodCode,
    transactionReference: d.transactionReference,
    donorName: profile.fullName,
    history: buildHistory(d.orderStatus, donationDate),
    distribution: d.distribution
      ? {
          location: d.distribution.location ?? null,
          partnerOrganization: d.distribution.partnerOrganization ?? null,
          note: d.distribution.note ?? null,
          peopleServed: d.distribution.peopleServed ?? d.peopleCount,
          photos: d.distribution.photos ?? [],
        }
      : undefined,
  };
});

const notifications: DemoNotification[] = data.notifications.map((n) => ({
  id: n.id,
  type: n.type,
  title: n.title,
  body: n.body,
  createdAt: iso(-n.hoursAgo * 3_600_000),
  readAt: n.read ? iso(-n.hoursAgo * 3_600_000 + 60_000) : null,
  donationReference: n.donationReference,
}));

const quotes = new Map<
  string,
  { mealId: string; peopleCount: number; deliveryFeeMinor: number; expiresAt: number }
>();

// ------------------------------------------------------------------ mapping

const mealById = (id: string): Meal =>
  data.meals.find((m) => m.id === id) ?? (data.meals[0] as Meal);

function toSummary(d: DemoDonation): DonationSummary {
  const meal = mealById(d.mealId);
  const meta = orderStatusMeta[d.orderStatus];
  return {
    id: d.id,
    publicReference: d.publicReference,
    meal: { id: meal.id, name: meal.name, nameUr: meal.nameUr ?? null, imageUrl: meal.imageUrl },
    peopleCount: d.peopleCount,
    unitPrice: meal.unitPrice,
    mealSubtotal: money(meal.unitPrice.amountMinor * d.peopleCount),
    deliveryFee: money(d.deliveryFeeMinor ?? 0),
    fulfilmentType: d.fulfilmentType ?? 'CHARITY_DISTRIBUTION',
    totalAmount: money(meal.unitPrice.amountMinor * d.peopleCount + (d.deliveryFeeMinor ?? 0)),
    donationDate: d.donationDate,
    distributionDate: d.distributionDate,
    orderStatus: d.orderStatus,
    // The demo mirrors the server's projection: a completed order is paid.
    paymentStatus:
      d.orderStatus === 'NEW' || d.orderStatus === 'CANCELLED' ? 'PENDING' : 'PAID',
    distributionStatus:
      d.orderStatus === 'COMPLETED'
        ? 'VERIFIED'
        : d.orderStatus === 'DISTRIBUTED'
          ? 'DISTRIBUTED'
          : meta.terminal
            ? 'NOT_SCHEDULED'
            : d.orderStatus === 'NEW'
              ? 'NOT_SCHEDULED'
              : 'SCHEDULED',
  };
}

function toDonation(d: DemoDonation): Donation {
  const method = data.paymentMethods.find((m) => m.code === d.paymentMethodCode);
  const purposeLabel =
    data.config.purposes.find((p) => p.code === d.purpose)?.label ?? d.purpose;

  return {
    ...toSummary(d),
    purpose: d.purpose,
    purposeLabel,
    purposeDetails: d.purposeDetails,
    donorName: d.donorName,
    requiresDistributionEvidence: d.requiresDistributionEvidence ?? false,
    recipientName: d.recipientName ?? null,
    deliveryInstruction: d.deliveryInstruction ?? null,
    anonymous: d.anonymous,
    shareDistributionPhotos: d.shareDistributionPhotos,
    paymentMethod: method
      ? { code: method.code, name: method.name, instrumentLabel: null }
      : null,
    transactionReference: d.transactionReference,
    createdAt: d.donationDate,
    updatedAt: d.history[d.orderStatus] ?? d.donationDate,
  };
}

function toTracking(d: DemoDonation): DonationTracking {
  const currentIndex = TRACKING_STEPS.indexOf(d.orderStatus);

  const steps = TRACKING_STEPS.map((status, index) => {
    let state: 'complete' | 'current' | 'pending';
    if (currentIndex < 0) {
      state = d.history[status] ? 'complete' : 'pending';
    } else if (index < currentIndex) {
      state = 'complete';
    } else if (index === currentIndex) {
      state = status === 'COMPLETED' ? 'complete' : 'current';
    } else {
      state = 'pending';
    }
    return {
      status,
      label: orderStatusMeta[status].label,
      occurredAt: d.history[status] ?? null,
      state,
    };
  });

  const exceptional: Partial<Record<OrderStatus, string>> = {
    CANCELLED: 'This donation was cancelled.',
    PAYMENT_FAILED: 'We could not take your payment. You can try again.',
    REFUNDED: 'This donation has been refunded.',
  };

  return {
    donation: toSummary(d),
    steps,
    exception: exceptional[d.orderStatus]
      ? {
          status: d.orderStatus,
          label: orderStatusMeta[d.orderStatus].label,
          message: exceptional[d.orderStatus] as string,
        }
      : null,
  };
}

// ------------------------------------------------------------------- tiffin

/**
 * The kitchen's clock. Pakistan keeps no daylight saving, so the business
 * timezone is always five hours ahead of UTC and a fixed offset is exact.
 */
const KITCHEN_OFFSET = '+05:00';
const KITCHEN_OFFSET_MS = 5 * 3_600_000;
const kitchenDate = (instant: number): string =>
  new Date(instant + KITCHEN_OFFSET_MS).toISOString().slice(0, 10);
const kitchenInstant = (day: string, hhmm: string): number =>
  Date.parse(`${day}T${hhmm}:00${KITCHEN_OFFSET}`);
const kitchenIso = (instant: number): string =>
  `${new Date(instant + KITCHEN_OFFSET_MS).toISOString().slice(0, 19)}${KITCHEN_OFFSET}`;
const kitchenClock = (instant: number): string =>
  new Date(instant + KITCHEN_OFFSET_MS).toISOString().slice(11, 16);

const tiffinSettings = data.tiffin.settings;
/** The server's `MAX_ORDER_DAYS`: one order covers a month at most. */
const MAX_ORDER_DAYS = 30;

function tiffinConfig(): TiffinConfig {
  const pickupAvailable =
    tiffinSettings.pickup.enabled && tiffinSettings.pickup.address.trim().length > 0;
  const fulfilmentTypes: FulfilmentType[] = [];
  if (tiffinSettings.delivery.enabled) fulfilmentTypes.push('DOORSTEP_DELIVERY');
  if (pickupAvailable) fulfilmentTypes.push('SELF_PICKUP');
  const hasMenu = data.tiffin.sections.some((section) => section.items.length > 0);
  return {
    // The server's rule: switched on, serving a meal, able to hand food over,
    // and something on the menu.
    available:
      tiffinSettings.enabled &&
      tiffinSettings.slots.some((window) => window.enabled) &&
      fulfilmentTypes.length > 0 &&
      hasMenu,
    today: kitchenDate(Date.now()),
    timezone: 'Asia/Karachi',
    maxDaysAhead: tiffinSettings.maxDaysAhead,
    maxPersons: tiffinSettings.maxPersons,
    minDays: tiffinSettings.minDays,
    maxDays: MAX_ORDER_DAYS,
    schedules: tiffinSettings.schedules,
    slots: tiffinSettings.slots,
    fulfilmentTypes,
    pickup: {
      enabled: pickupAvailable,
      address: tiffinSettings.pickup.address || null,
      note: tiffinSettings.pickup.note,
    },
    delivery: {
      enabled: tiffinSettings.delivery.enabled,
      fee: money(tiffinSettings.delivery.feeMinor),
      cities: tiffinSettings.delivery.cities,
    },
  };
}

const menuItems = data.tiffin.sections.flatMap((section) =>
  section.items.map((item, index) => ({ ...item, section, sortOrder: index })),
);

function tiffinMenu(slot: ServiceSlot | null): TiffinMenu {
  return {
    sections: data.tiffin.sections
      .map((section) => ({
        id: section.id,
        name: section.name,
        nameUr: section.nameUr,
        description: section.description,
        descriptionUr: section.descriptionUr,
        isActive: true,
        sortOrder: section.sortOrder,
        items: section.items
          .filter((item) => !slot || item.serviceSlots.includes(slot))
          .map((item, index) => ({
            id: item.id,
            sectionId: section.id,
            name: item.name,
            nameUr: item.nameUr,
            description: item.description,
            descriptionUr: item.descriptionUr,
            price: money(item.priceMinor),
            imageUrl: item.imageUrl,
            serviceSlots: item.serviceSlots,
            maxQuantity: item.maxQuantity,
            isActive: true,
            sortOrder: index,
          })),
      }))
      // As on the server: no empty "Sweets" heading at breakfast.
      .filter((section) => section.items.length > 0),
  };
}

/** Mirrors the server's `slot_availability`, reasons and all. */
function tiffinAvailability(serviceDate: string): TiffinAvailability {
  const now = Date.now();
  const today = kitchenDate(now);
  const daysAhead = Math.round(
    (Date.parse(`${serviceDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY,
  );
  return {
    serviceDate,
    slots: tiffinSettings.slots.map((window): SlotAvailability => {
      const startsAt = kitchenInstant(serviceDate, window.start);
      const endsAt = kitchenInstant(serviceDate, window.end);
      const orderBy = startsAt - window.cutoffMinutes * 60_000;
      let reason: string | null = null;
      if (!tiffinSettings.enabled) reason = 'Tiffin orders are paused at the moment.';
      else if (!window.enabled) reason = `${window.label} is not being served.`;
      else if (daysAhead < 0) reason = 'That date has passed.';
      else if (daysAhead > tiffinSettings.maxDaysAhead) {
        reason =
          tiffinSettings.maxDaysAhead === 0
            ? 'Orders are taken for today only.'
            : `Orders open ${tiffinSettings.maxDaysAhead} days ahead.`;
      } else if (now >= orderBy) {
        reason = `Orders for ${window.label.toLowerCase()} closed at ${formatClock(kitchenClock(orderBy))}.`;
      }
      return {
        slot: window.slot,
        label: window.label,
        enabled: window.enabled,
        open: reason === null,
        startsAt: kitchenIso(startsAt),
        endsAt: kitchenIso(endsAt),
        orderBy: kitchenIso(orderBy),
        reason,
      };
    }),
  };
}

function assertTiffinWindowOpen(serviceDate: string, slot: ServiceSlot): void {
  const availability = tiffinAvailability(serviceDate).slots.find((entry) => entry.slot === slot);
  if (!availability) fail(422, 'tiffin.window_closed', 'Choose breakfast, lunch or dinner.');
  if (!availability.open) {
    fail(422, 'tiffin.window_closed', availability.reason ?? 'This meal is not available.');
  }
}

/** Mirrors the server's `price_tiffin` and the item checks around it. */
function priceTiffin(request: TiffinQuoteRequest): {
  lines: TiffinLine[];
  box: number;
  days: number;
  schedule: ServiceSchedule;
  dates: string[];
  fee: number;
} {
  const config = tiffinConfig();
  const days = request.days ?? config.minDays;
  if (days < config.minDays) {
    fail(422, 'tiffin.invalid', `Tiffin orders are for at least ${config.minDays} days.`);
  }
  if (days > config.maxDays) {
    fail(422, 'tiffin.invalid', `One order can cover up to ${config.maxDays} days.`);
  }
  const schedule = request.schedule ?? config.schedules[0]!;
  if (!config.schedules.includes(schedule)) {
    fail(422, 'tiffin.invalid', 'That delivery schedule is not offered at the moment.');
  }
  const dates = deliveryDates(request.serviceDate, days, schedule);
  const label = serviceSlotLabel[request.serviceSlot].toLowerCase();
  if (!request.items.length) fail(422, 'tiffin.invalid', 'Add at least one item to your tiffin.');
  if (request.persons < 1) fail(422, 'tiffin.invalid', 'A tiffin is for at least one person.');
  if (request.persons > config.maxPersons) {
    fail(422, 'tiffin.invalid', `One order can serve up to ${config.maxPersons} persons.`);
  }
  const lines = request.items.map((line): TiffinLine => {
    const item = menuItems.find((candidate) => candidate.id === line.menuItemId);
    if (!item) {
      fail(
        422,
        'tiffin.item_unavailable',
        'An item in your tiffin is no longer on the menu. Please review your order.',
      );
    }
    if (!item.serviceSlots.includes(request.serviceSlot)) {
      fail(422, 'tiffin.item_not_in_slot', `${item.name} is not served at ${label}.`);
    }
    if (line.quantity < 1) fail(422, 'tiffin.invalid', 'Each item needs a quantity of at least one.');
    if (line.quantity > item.maxQuantity) {
      fail(422, 'tiffin.invalid', `You can add up to ${item.maxQuantity} of one item.`);
    }
    return {
      menuItemId: item.id,
      sectionName: item.section.name,
      name: item.name,
      nameUr: item.nameUr,
      unitPrice: money(item.priceMinor),
      quantity: line.quantity,
      lineTotal: money(item.priceMinor * line.quantity),
    };
  });
  if (!config.fulfilmentTypes.includes(request.fulfilmentType)) {
    fail(
      422,
      'tiffin.invalid',
      request.fulfilmentType === 'DOORSTEP_DELIVERY'
        ? 'Pick & drop is not available at the moment.'
        : 'Pickup is not available at the moment.',
    );
  }
  let fee = 0;
  if (request.fulfilmentType === 'DOORSTEP_DELIVERY') {
    const city = (request.deliveryCity ?? '').trim().toLowerCase();
    if (!config.delivery.cities.some((served) => served.toLowerCase() === city)) {
      fail(
        422,
        'tiffin.invalid',
        `Pick & drop is only available in ${config.delivery.cities.join(' or ') || 'any city'}.`,
      );
    }
    // Pick & drop is a trip a meal.
    fee = config.delivery.fee.amountMinor * dates.length;
  }
  return {
    lines,
    box: lines.reduce((sum, line) => sum + line.lineTotal.amountMinor, 0),
    days,
    schedule,
    dates,
    fee,
  };
}

interface DemoTiffinOrder {
  id: string;
  publicReference: string;
  createdAt: string;
  serviceDate: string;
  serviceSlot: ServiceSlot;
  persons: number;
  days: number;
  schedule: ServiceSchedule;
  lines: TiffinLine[];
  fulfilmentType: FulfilmentType;
  orderStatus: OrderStatus;
  deliveryFeeMinor: number;
  address: DeliveryAddress | null;
  customer: TiffinCustomer;
  note: string | null;
  paymentMethodCode: string;
  transactionReference: string | null;
  history: Partial<Record<OrderStatus, string>>;
  idempotencyKey?: string;
}

const tiffinOrders: DemoTiffinOrder[] = data.tiffin.orders.map((row) => {
  const createdAt = iso(row.placedDayOffset * DAY - 4 * 3_600_000);
  const serviceDate = kitchenDate(START + row.serviceDayOffset * DAY);
  const priced = priceLinesForSeed(row.items);
  return {
    id: row.id,
    publicReference: row.publicReference,
    createdAt,
    serviceDate,
    serviceSlot: row.serviceSlot,
    persons: row.persons,
    days: row.days,
    schedule: row.schedule,
    lines: priced,
    fulfilmentType: row.fulfilmentType,
    orderStatus: row.orderStatus,
    deliveryFeeMinor:
      row.fulfilmentType === 'DOORSTEP_DELIVERY'
        ? tiffinSettings.delivery.feeMinor * deliveryDates(serviceDate, row.days, row.schedule).length
        : 0,
    address: row.address,
    customer: { fullName: profile.fullName, mobileNumber: profile.mobileNumber ?? '', email: null },
    note: row.note,
    paymentMethodCode: row.paymentMethodCode,
    transactionReference: row.transactionReference,
    history: buildTiffinHistory(row.orderStatus, createdAt),
  };
});

/** Seeded orders are priced from the menu without the live checks. */
function priceLinesForSeed(items: { menuItemId: string; quantity: number }[]): TiffinLine[] {
  return items.flatMap((line) => {
    const item = menuItems.find((candidate) => candidate.id === line.menuItemId);
    return item
      ? [
          {
            menuItemId: item.id,
            sectionName: item.section.name,
            name: item.name,
            nameUr: item.nameUr,
            unitPrice: money(item.priceMinor),
            quantity: line.quantity,
            lineTotal: money(item.priceMinor * line.quantity),
          },
        ]
      : [];
  });
}

/** A tiffin's steps happen in hours, not days. */
function buildTiffinHistory(status: OrderStatus, placedAt: string): DemoTiffinOrder['history'] {
  const reached = TRACKING_STEPS.indexOf(status);
  const steps = reached >= 0 ? TRACKING_STEPS.slice(0, reached + 1) : (['NEW'] as const);
  const start = new Date(placedAt).getTime();
  const history: DemoTiffinOrder['history'] = {};
  steps.forEach((step, index) => {
    history[step] = new Date(start + index * 25 * 60_000).toISOString();
  });
  return history;
}

const tiffinQuotes = new Map<string, { quote: TiffinQuote; deliveryCity: string | null }>();

const TIMES = '×';

/** Mirrors the server's preview: four items, then how many more. */
function itemsPreview(lines: TiffinLine[], urdu: boolean): string {
  const names = lines.map((line) => {
    const name = urdu ? line.nameUr ?? line.name : line.name;
    return line.quantity > 1 ? `${name} ${TIMES}${line.quantity}` : name;
  });
  const comma = urdu ? '\u060c ' : ', ';
  if (names.length <= 4) return names.join(comma);
  const more = urdu ? `+${names.length - 4} مزید` : `+${names.length - 4} more`;
  return `${names.slice(0, 4).join(comma)} ${more}`;
}

/** The days an order's schedule delivers on. */
const meals = (order: DemoTiffinOrder): string[] =>
  deliveryDates(order.serviceDate, order.days, order.schedule);

function tiffinSummary(order: DemoTiffinOrder): TiffinOrderSummary {
  const box = order.lines.reduce((sum, line) => sum + line.lineTotal.amountMinor, 0);
  return {
    id: order.id,
    publicReference: order.publicReference,
    orderType: 'TIFFIN',
    serviceDate: order.serviceDate,
    serviceSlot: order.serviceSlot,
    serviceLabel: serviceSlotLabel[order.serviceSlot],
    persons: order.persons,
    days: order.days,
    endDate: tiffinEndDate(order.serviceDate, order.days),
    schedule: order.schedule,
    deliveryDates: meals(order),
    deliveries: meals(order).length,
    itemCount: order.lines.length,
    itemsPreview: itemsPreview(order.lines, false),
    itemsPreviewUr: itemsPreview(order.lines, true),
    totalAmount: money(box * order.persons * meals(order).length + order.deliveryFeeMinor),
    fulfilmentType: order.fulfilmentType,
    orderStatus: order.orderStatus,
    statusLabel: tiffinStatusLabel(order.orderStatus, order.fulfilmentType),
    paymentStatus:
      order.orderStatus === 'NEW' || order.orderStatus === 'CANCELLED'
        ? 'PENDING'
        : order.orderStatus === 'PAYMENT_FAILED'
          ? 'FAILED'
          : order.orderStatus === 'REFUNDED'
            ? 'REFUNDED'
            : 'PAID',
    createdAt: order.createdAt,
  };
}

const TIFFIN_EXCEPTIONS: Partial<Record<OrderStatus, string>> = {
  PAYMENT_FAILED: 'We could not take your payment. You can try again.',
  CANCELLED: 'This order was cancelled.',
  REFUND_PENDING: 'A refund is being processed.',
  REFUNDED: 'This order has been refunded.',
};

function toTiffinOrder(order: DemoTiffinOrder): TiffinOrder {
  const box = order.lines.reduce((sum, line) => sum + line.lineTotal.amountMinor, 0);
  const window =
    tiffinSettings.slots.find((slot) => slot.slot === order.serviceSlot) ?? tiffinSettings.slots[0]!;
  const method = data.paymentMethods.find((m) => m.code === order.paymentMethodCode);
  const currentIndex = TRACKING_STEPS.indexOf(order.orderStatus);
  const exception = TIFFIN_EXCEPTIONS[order.orderStatus];
  return {
    ...tiffinSummary(order),
    items: order.lines,
    boxTotal: money(box),
    subtotal: money(box * order.persons * meals(order).length),
    deliveryFee: money(order.deliveryFeeMinor),
    customerName: order.customer.fullName,
    customerMobile: order.customer.mobileNumber,
    customerEmail: order.customer.email ?? null,
    address: order.address,
    pickupAddress: order.fulfilmentType === 'SELF_PICKUP' ? tiffinSettings.pickup.address : null,
    note: order.note,
    serviceStartsAt: kitchenIso(kitchenInstant(order.serviceDate, window.start)),
    serviceEndsAt: kitchenIso(kitchenInstant(order.serviceDate, window.end)),
    paymentMethod: method ? { code: method.code, name: method.name, instrumentLabel: null } : null,
    transactionReference: order.transactionReference,
    steps: TRACKING_STEPS.map((status, index) => ({
      status,
      label: tiffinStatusLabel(status, order.fulfilmentType),
      occurredAt: order.history[status] ?? null,
      state:
        currentIndex < 0
          ? order.history[status]
            ? 'complete'
            : 'pending'
          : index < currentIndex
            ? 'complete'
            : index === currentIndex
              ? status === 'COMPLETED'
                ? 'complete'
                : 'current'
              : 'pending',
    })),
    exception: exception
      ? {
          status: order.orderStatus,
          label: tiffinStatusLabel(order.orderStatus, order.fulfilmentType),
          message: exception,
        }
      : null,
    updatedAt: order.history[order.orderStatus] ?? order.createdAt,
  };
}

// ----------------------------------------------------------------- handlers

const notFound = (message: string) => {
  const error = new Error(message) as Error & { status: number; code: string };
  error.status = 404;
  error.code = 'resource.not_found';
  throw error;
};

export interface DemoRequest {
  method: string;
  /** Path relative to /api/v1, without a leading slash. */
  path: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export function handle(request: DemoRequest): unknown {
  const { method, path, body, query } = request;
  const segments = path.split('/').filter(Boolean);
  const [head, second, third] = segments;

  // ----------------------------------------------------------- public data
  if (method === 'GET' && path === 'home/stats') {
    // Recompute so a donation made in the demo visibly moves the numbers.
    const fulfilled = donations.filter(
      (d) => d.orderStatus === 'COMPLETED' || d.orderStatus === 'DISTRIBUTED',
    );
    const extra = fulfilled.reduce((sum, d) => sum + d.peopleCount, 0);
    return {
      ...data.stats,
      mealsServed: data.stats.mealsServed + extra,
      generatedAt: new Date().toISOString(),
    } satisfies ImpactStats;
  }

  if (method === 'GET' && path === 'how-it-works') return data.howItWorks;
  if (method === 'GET' && path === 'meals') return data.meals.filter((m) => m.isActive);
  if (method === 'GET' && head === 'meals' && second) {
    return data.meals.find((m) => m.id === second) ?? notFound('Meal not found.');
  }
  if (method === 'GET' && path === 'payment-methods') {
    return data.paymentMethods.filter((m) => m.enabled);
  }
  if (method === 'GET' && path === 'config') {
    // Worked out per request: "today" moves, and so does what is open.
    return { ...data.config, tiffin: tiffinConfig() } satisfies AppConfig;
  }

  // ----------------------------------------------------------------- tiffin
  if (method === 'GET' && path === 'tiffin/menu') {
    return tiffinMenu((query?.slot as ServiceSlot | undefined) ?? null);
  }

  if (method === 'GET' && path === 'tiffin/availability') {
    return tiffinAvailability((query?.date as string | undefined) ?? kitchenDate(Date.now()));
  }

  if (method === 'POST' && path === 'tiffin/quote') {
    const input = body as TiffinQuoteRequest;
    assertTiffinWindowOpen(input.serviceDate, input.serviceSlot);
    const priced = priceTiffin(input);
    const token = uid('tquote');
    const quote: TiffinQuote = {
      quoteToken: token,
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
      serviceDate: input.serviceDate,
      serviceSlot: input.serviceSlot,
      persons: input.persons,
      days: priced.days,
      endDate: tiffinEndDate(input.serviceDate, priced.days),
      schedule: priced.schedule,
      deliveryDates: priced.dates,
      deliveries: priced.dates.length,
      items: priced.lines,
      boxTotal: money(priced.box),
      subtotal: money(priced.box * input.persons * priced.dates.length),
      deliveryFee: money(priced.fee),
      totalAmount: money(priced.box * input.persons * priced.dates.length + priced.fee),
      fulfilmentType: input.fulfilmentType,
    };
    tiffinQuotes.set(token, {
      quote,
      deliveryCity:
        input.fulfilmentType === 'DOORSTEP_DELIVERY' ? (input.deliveryCity ?? '').trim() : null,
    });
    return quote;
  }

  if (method === 'POST' && path === 'tiffin/orders') {
    const input = body as CreateTiffinOrderRequest;

    // Replaying a key returns the original, exactly as the server does.
    const replayed = tiffinOrders.find((order) => order.idempotencyKey === input.idempotencyKey);
    if (replayed) return toTiffinOrder(replayed);

    const held = tiffinQuotes.get(input.quoteToken);
    if (!held) {
      fail(422, 'tiffin.quote_missing', 'Your price could not be found. Please review your order again.');
    }
    const { quote } = held;
    const sameLines =
      quote.items.length === input.items.length &&
      input.items.every((line) =>
        quote.items.some((q) => q.menuItemId === line.menuItemId && q.quantity === line.quantity),
      );
    if (
      quote.serviceDate !== input.serviceDate ||
      quote.serviceSlot !== input.serviceSlot ||
      quote.persons !== input.persons ||
      (input.days !== undefined && quote.days !== input.days) ||
      (input.schedule !== undefined && quote.schedule !== input.schedule) ||
      quote.fulfilmentType !== input.fulfilmentType ||
      !sameLines
    ) {
      fail(409, 'tiffin.quote_mismatch', 'Your order changed. Please review the total again.');
    }
    assertTiffinWindowOpen(input.serviceDate, input.serviceSlot);
    if (input.fulfilmentType === 'DOORSTEP_DELIVERY') {
      if (!input.address) fail(422, 'validation_error', 'Add the address to deliver to.');
      if ((held.deliveryCity ?? '').toLowerCase() !== input.address.city.trim().toLowerCase()) {
        fail(409, 'tiffin.quote_mismatch', 'The delivery city changed. Please review the total again.');
      }
    }
    tiffinQuotes.delete(input.quoteToken);

    const now = new Date().toISOString();
    const created: DemoTiffinOrder = {
      id: uid('tif'),
      publicReference: newReference(),
      createdAt: now,
      serviceDate: input.serviceDate,
      serviceSlot: input.serviceSlot,
      persons: quote.persons,
      days: quote.days,
      schedule: quote.schedule,
      lines: quote.items,
      fulfilmentType: input.fulfilmentType,
      orderStatus: 'NEW',
      deliveryFeeMinor: quote.deliveryFee.amountMinor,
      address: input.fulfilmentType === 'DOORSTEP_DELIVERY' ? input.address : null,
      customer: input.customer,
      note: input.note,
      paymentMethodCode: input.paymentMethodCode,
      transactionReference: null,
      history: { NEW: now },
      idempotencyKey: input.idempotencyKey,
    };
    tiffinOrders.unshift(created);
    return toTiffinOrder(created);
  }

  if (method === 'GET' && head === 'tiffin' && second === 'orders' && third) {
    const order = tiffinOrders.find((candidate) => candidate.id === third);
    return order ? toTiffinOrder(order) : notFound('Order not found.');
  }

  if (method === 'GET' && path === 'me/tiffin-orders') {
    const filter = (query?.filter as DonationFilter) ?? 'all';
    const sort = (query?.sort as DonationSort) ?? 'newest';
    const sorted = tiffinOrders
      .filter((order) => matchesFilter(order.orderStatus, filter))
      .sort((a, b) => {
        if (sort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
        if (sort === 'upcoming') return a.serviceDate.localeCompare(b.serviceDate);
        return b.createdAt.localeCompare(a.createdAt);
      });
    return {
      items: sorted.map(tiffinSummary),
      total: sorted.length,
      page: 1,
      pageSize: sorted.length || 1,
      hasMore: false,
    } satisfies Paginated<TiffinOrderSummary>;
  }

  // -------------------------------------------------------------- donations
  if (method === 'POST' && path === 'donations/quote') {
    const input = body as {
      mealId: string;
      peopleCount: number;
      fulfilmentType?: FulfilmentType;
      deliveryCity?: string;
    };
    const meal = data.meals.find((m) => m.id === input.mealId) ?? notFound('Meal not found.');
    const fulfilmentType = input.fulfilmentType ?? 'CHARITY_DISTRIBUTION';
    const policy = data.config.delivery;

    // Mirrors the server: the fee and the city rule are applied together, so
    // the demo cannot quote a delivery to somewhere the real thing refuses.
    if (fulfilmentType === 'DOORSTEP_DELIVERY') {
      if (!policy.enabled) {
        fail(422, 'delivery.unavailable', 'Doorstep delivery is not available at the moment.');
      }
      const permitted = policy.cities.some(
        (c) => c.trim().toLowerCase() === (input.deliveryCity ?? '').trim().toLowerCase(),
      );
      if (!permitted) {
        fail(
          422,
          'delivery.city_not_served',
          `Doorstep delivery is only available in ${policy.cities.join(' or ')}.`,
        );
      }
    }

    const subtotal = meal.unitPrice.amountMinor * input.peopleCount;
    const fee = fulfilmentType === 'DOORSTEP_DELIVERY' ? policy.fee.amountMinor : 0;
    const token = uid('quote');
    quotes.set(token, {
      mealId: input.mealId,
      peopleCount: input.peopleCount,
      deliveryFeeMinor: fee,
      expiresAt: Date.now() + 30 * 60_000,
    });
    return {
      mealId: meal.id,
      mealName: meal.name,
      peopleCount: input.peopleCount,
      unitPrice: meal.unitPrice,
      mealSubtotal: money(subtotal),
      deliveryFee: money(fee),
      totalAmount: money(subtotal + fee),
      fulfilmentType,
      quoteToken: token,
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
    } satisfies Quote;
  }

  if (method === 'POST' && path === 'donations') {
    const input = body as CreateDonationRequest;

    // Replaying a key returns the original, exactly as the server does.
    const replayed = donations.find(
      (d) => (d as DemoDonation & { idempotencyKey?: string }).idempotencyKey === input.idempotencyKey,
    );
    if (replayed) return toDonation(replayed);

    const quote = quotes.get(input.quoteToken);
    if (!quote) {
      const error = new Error(
        'Your price quote could not be found. Please review your donation again.',
      ) as Error & { status: number; code: string };
      error.status = 422;
      error.code = 'donation.quote_missing';
      throw error;
    }
    quotes.delete(input.quoteToken);

    const created: DemoDonation & { idempotencyKey?: string } = {
      id: uid('don'),
      publicReference: newReference(),
      mealId: input.mealId,
      peopleCount: input.peopleCount,
      purpose: input.purpose,
      purposeDetails: input.purposeDetails,
      donationDate: new Date().toISOString(),
      distributionDate: input.distributionDate,
      orderStatus: 'NEW',
      anonymous: input.donor.anonymous,
      shareDistributionPhotos: input.donor.shareDistributionPhotos,
      paymentMethodCode: input.paymentMethodCode,
      transactionReference: null,
      donorName: input.donor.fullName ?? null,
      deliveryFeeMinor: quote.deliveryFeeMinor ?? 0,
      fulfilmentType: input.recipient?.fulfilmentType ?? 'CHARITY_DISTRIBUTION',
      recipientName: input.recipient?.recipientName ?? null,
      deliveryInstruction: input.recipient?.instruction ?? null,
      requiresDistributionEvidence: input.requiresDistributionEvidence ?? false,
      history: { NEW: new Date().toISOString() },
      idempotencyKey: input.idempotencyKey,
    };
    donations.unshift(created);
    return toDonation(created);
  }

  if (method === 'POST' && path === 'donations/lookup') {
    const input = body as { publicReference: string; mobileNumber: string };
    const found = donations.find(
      (d) => d.publicReference.toUpperCase() === input.publicReference.toUpperCase().trim(),
    );
    return found ? toDonation(found) : notFound('We could not find a donation with those details.');
  }

  if (head === 'donations' && second) {
    const donation = donations.find((d) => d.id === second || d.publicReference === second);
    if (!donation) return notFound('Donation not found.');

    if (method === 'GET' && !third) return toDonation(donation);
    if (method === 'GET' && third === 'tracking') return toTracking(donation);

    if (method === 'GET' && third === 'proof') {
      if (!canShowProofPhotos(donation.orderStatus, true)) {
        const error = new Error('Proof of distribution is not available yet.') as Error & {
          status: number;
          code: string;
        };
        error.status = 404;
        error.code = 'proof.not_available';
        throw error;
      }

      const dist = donation.distribution;
      // Consent decides whether URLs exist at all — the demo withholds them
      // the same way the server does, rather than hiding them in the client.
      const consented = donation.shareDistributionPhotos;
      const photos = consented ? (dist?.photos ?? []) : [];

      return {
        donationId: donation.id,
        peopleServed: dist?.peopleServed ?? donation.peopleCount,
        distributionDate: donation.distributionDate,
        location: dist?.location ?? null,
        partnerOrganization: dist?.partnerOrganization ?? null,
        note: dist?.note ?? null,
        verifiedAt: donation.history.COMPLETED ?? null,
        photos: photos.map((url, index) => ({
          id: `${donation.id}-photo-${index}`,
          url,
          caption: null,
        })),
        photosWithheldReason: photos.length
          ? null
          : consented
            ? 'NOT_UPLOADED'
            : 'NO_CONSENT',
        certificateUrl: null,
      } satisfies DistributionProof;
    }
  }

  // --------------------------------------------------------------- payments
  if (method === 'POST' && path === 'payments/initiate') {
    const input = body as { donationId: string; paymentMethodCode: string };
    // One payment path for both kinds of order, as on the server.
    const donation: DemoDonation | DemoTiffinOrder | undefined =
      donations.find((d) => d.id === input.donationId) ??
      tiffinOrders.find((order) => order.id === input.donationId);
    if (!donation) return notFound('Order not found.');

    donation.paymentMethodCode = input.paymentMethodCode;

    if (input.paymentMethodCode === 'bank_transfer') {
      // Offline rail: stays pending, with instructions, like the real thing.
      return {
        paymentId: uid('pay'),
        status: 'PENDING',
        redirectUrl: null,
        instructions: [
          { label: 'Account Title', value: 'Meal4ALL Welfare Trust' },
          { label: 'Payment Reference', value: `MFA-${donation.publicReference}` },
        ],
        reference: `MFA-${donation.publicReference}`,
        instrumentLabel: null,
        simulated: false,
        testMode: false,
      } satisfies PaymentIntent;
    }

    const reference = `${input.paymentMethodCode.slice(0, 2).toUpperCase()}${Date.now()
      .toString()
      .slice(-9)}`;
    donation.transactionReference = reference;
    donation.orderStatus = 'PAYMENT_CONFIRMED';
    donation.history.PAYMENT_CONFIRMED = new Date().toISOString();

    return {
      paymentId: uid('pay'),
      status: 'PAID',
      redirectUrl: null,
      instructions: null,
      reference,
      instrumentLabel: null,
      simulated: false,
      testMode: false,
    } satisfies PaymentIntent;
  }

  // --------------------------------------------------------------------- me
  if (method === 'GET' && path === 'me') return profile;

  if (method === 'PATCH' && path === 'me') {
    Object.assign(profile, body as Partial<DonorProfile>);
    return profile;
  }

  if (method === 'GET' && path === 'me/donations') {
    const filter = (query?.filter as DonationFilter) ?? 'all';
    const sort = (query?.sort as DonationSort) ?? 'newest';

    const matched = donations.filter((d) => matchesFilter(d.orderStatus, filter));
    const sorted = [...matched].sort((a, b) => {
      if (sort === 'oldest') return a.donationDate.localeCompare(b.donationDate);
      if (sort === 'upcoming') return a.distributionDate.localeCompare(b.distributionDate);
      return b.donationDate.localeCompare(a.donationDate);
    });

    return {
      items: sorted.map(toSummary),
      total: sorted.length,
      page: 1,
      pageSize: sorted.length || 1,
      hasMore: false,
    } satisfies Paginated<DonationSummary>;
  }

  if (method === 'GET' && path === 'me/impact') {
    const counted = donations.filter(
      (d) => d.orderStatus === 'COMPLETED' || d.orderStatus === 'DISTRIBUTED',
    );
    const totalMinor = counted.reduce(
      (sum, d) => sum + mealById(d.mealId).unitPrice.amountMinor * d.peopleCount,
      0,
    );
    const oldest = [...donations].sort((a, b) =>
      a.donationDate.localeCompare(b.donationDate),
    )[0];

    return {
      totalDonations: donations.length,
      totalPeopleHelped: counted.reduce((sum, d) => sum + d.peopleCount, 0),
      totalAmountDonated: money(totalMinor),
      since: oldest?.donationDate ?? null,
    } satisfies DonorImpact;
  }

  if (method === 'GET' && path === 'me/notifications') {
    return {
      items: notifications as NotificationItem[],
      total: notifications.length,
      page: 1,
      pageSize: notifications.length || 1,
      hasMore: false,
    } satisfies Paginated<NotificationItem>;
  }

  if (method === 'GET' && path === 'me/notifications/unread-count') {
    return { count: notifications.filter((n) => n.readAt === null).length };
  }

  if (method === 'POST' && path === 'me/notifications/read') {
    const only = query?.notificationId as string | undefined;
    notifications.forEach((n) => {
      if (!only || n.id === only) n.readAt = n.readAt ?? new Date().toISOString();
    });
    return undefined;
  }

  if (method === 'PATCH' && path === 'me/notification-preferences') {
    const patch = body as Record<string, boolean>;
    const prefs = profile.preferences;
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'channelEmail') prefs.channels.email = value;
      else if (key === 'channelSms') prefs.channels.sms = value;
      else if (key === 'channelWhatsapp') prefs.channels.whatsapp = value;
      else if (key in prefs) (prefs as unknown as Record<string, boolean>)[key] = value;
    }
    return prefs;
  }

  // ------------------------------------------------------------------- auth
  if (method === 'POST' && (path === 'auth/login' || path === 'auth/register')) {
    return {
      accessToken: 'demo-access-token',
      refreshToken: 'demo-refresh-token',
      expiresIn: 3600,
      tokenType: 'Bearer',
    };
  }
  if (method === 'POST' && path === 'auth/logout') return undefined;

  const error = new Error(`Demo mode has no handler for ${method} ${path}`) as Error & {
    status: number;
    code: string;
  };
  error.status = 501;
  error.code = 'demo.unhandled';
  throw error;
}

/** Resets the store. Used by tests. */
export function _resetForTests(): void {
  quotes.clear();
  tiffinQuotes.clear();
}
